/**
 * addinLaunchIO — the add-in's `LaunchIO` implementation, per plan
 * glittery-stargazing-treehouse Phase 3 step 1.
 *
 * Thin transport wrapper around the existing `orchestratorApi`. No policy
 * lives here — `resolveLaunchTerm` / `assembleLaunchInputs` /
 * `buildLaunchMetadata` / `runPrefill` (all in
 * `@product-app/common/workflows/launch/core/`) decide WHAT to send; this
 * file only decides HOW to send it over the add-in's existing HTTP client.
 *
 * `submit`:
 *   - Wraps `orchestratorApi.triggerWorkflow(workflowId, inputs, metadata)`.
 *   - `orchestratorApi.request` (the module-private fetch helper) throws an
 *     `ApiError` (`status: number | null`, `response_body: unknown`) on any
 *     non-2xx response — see `InstanceDetailDrawer.tsx`'s existing 409
 *     handling (~1647-1675), which reads `err.status` and, via
 *     `refusalReasonCode`, `err.response_body`. `submit` catches exactly
 *     that shape and turns it into the discriminated `LaunchSubmitResult`
 *     the core's `LaunchIO` contract requires, so `resolveLaunchTerm` /
 *     `assembleLaunchInputs` callers never need to know about `ApiError`.
 *   - A non-`ApiError` throw (network/transport failure `orchestratorApi`
 *     could not classify as an HTTP response) is RE-THROWN, not converted —
 *     per `launchIO.ts`'s own docstring, that is the one case left to the
 *     host to catch.
 *
 * ⚠️ `idempotency_key` stays INSIDE `inputs`, exactly as today — it is never
 * pulled out and sent as a top-level `LaunchSubmitRequest.idempotencyKey`.
 * The .NET `TriggerRequest` the product API deserialises into reads only a
 * TOP-LEVEL `idempotency_key`, so the add-in's substrate dedup has never
 * actually engaged (the key has only ever ridden inside `inputs`, where the
 * .NET model ignores it). That is a pre-existing bug, tracked as ADO story
 * 28067 item 7 ("TS<->C# contract pins for `idempotency_key` passthrough") —
 * fixing it switches real dedup on and needs its own verification, so this
 * file preserves today's (broken) placement rather than silently fixing it.
 *
 * `triggerPrefill` / `pollPrefill` / `storeBriefPrefill` wrap
 * `orchestratorApi`'s existing prefill methods. `orchestratorApi.triggerPrefill`
 * / `.reanalysePrefill` build their OWN `inputs` (via the now-shared
 * `buildPrefillInputs`) and their OWN idempotency key from raw args — they do
 * not accept a pre-built `inputs` object or an explicit key. The core's
 * `runPrefill` (`runPrefill.ts`), on the other hand, already builds `inputs`
 * (via the SAME `buildPrefillInputs`, imported from
 * `core/prefillInputs.ts` — see `orchestratorApi.ts`'s own import of it
 * post-step-2) and computes the key itself, choosing between the
 * `prefill:${hash}` and `prefill:${hash}:reanalyse:${ts}` wire formats via
 * its `fresh` flag. Since both sides now hash identically, this adapter
 * reconstructs the raw args FROM the already-built `inputs` (same declared
 * keys `buildPrefillInputs` always produces) and picks `reanalysePrefill` vs
 * `triggerPrefill` by sniffing the `:reanalyse:` marker the core's key
 * already carries — preserving both wire key formats without a second
 * hashing path or a new orchestratorApi method.
 */
import {
  orchestratorApi,
  ApiError,
  type OrchestratorApi,
} from "../services/orchestratorApi";
import { logger } from "../obs/logger";
import type {
  LaunchIO,
  LaunchSubmitRequest,
  LaunchSubmitResult,
  PollPrefillResult,
  PrefillConfidence,
  StoreBriefPrefillArgs,
  TriggerPrefillResult,
} from "@product-app/common/workflows/launch/core/launchIO";

export function createAddinLaunchIO(api: OrchestratorApi = orchestratorApi): LaunchIO {
  return {
    async submit(req: LaunchSubmitRequest): Promise<LaunchSubmitResult> {
      try {
        const result = await api.triggerWorkflow(
          req.workflowId,
          req.inputs,
          req.metadata,
        );
        return { ok: true, instanceId: result.instance_id, body: result };
      } catch (err) {
        if (err instanceof ApiError) {
          return {
            ok: false,
            status: err.status ?? 0,
            body: err.response_body,
            message: err.message,
          };
        }
        // Network/transport failure — not an HTTP response the core can
        // reason about. Left to the host's own catch, per launchIO.ts.
        throw err;
      }
    },

    async triggerPrefill(
      inputs: Record<string, unknown>,
      idempotencyKey: string,
    ): Promise<TriggerPrefillResult> {
      const targetWorkflowId = String(inputs.target_workflow_id ?? "");
      const clientSk =
        typeof inputs.client_sk === "number" ? inputs.client_sk : null;
      const sourceDocumentText =
        typeof inputs.source_document_text === "string"
          ? inputs.source_document_text
          : "";
      const alreadyExtracted =
        (inputs.already_extracted as Record<string, unknown> | undefined) ??
        {};
      const attachmentText =
        typeof inputs.attachment_text === "string"
          ? inputs.attachment_text
          : "";
      // The email's identity, when the core put one on the inputs. Dropping it
      // here is what left a brief opened from the Today list (its email not the
      // selected item, so no body to send) with nothing for workflow_prefill to
      // recover its source from.
      const internetMessageId =
        typeof inputs.internet_message_id === "string"
          ? inputs.internet_message_id
          : undefined;
      const mailboxAddress =
        typeof inputs.mailbox_address === "string"
          ? inputs.mailbox_address
          : undefined;
      // The ONE signal that distinguishes a RE-ANALYSE key from a normal
      // trigger key — see this module's docstring.
      const fresh = idempotencyKey.includes(":reanalyse:");
      const result = fresh
        ? await api.reanalysePrefill(
            targetWorkflowId,
            clientSk,
            sourceDocumentText,
            alreadyExtracted,
            attachmentText,
            internetMessageId,
            mailboxAddress,
          )
        : await api.triggerPrefill(
            targetWorkflowId,
            clientSk,
            sourceDocumentText,
            alreadyExtracted,
            attachmentText,
            internetMessageId,
            mailboxAddress,
          );
      return { instanceId: result.instance_id };
    },

    async pollPrefill(instanceId: string): Promise<PollPrefillResult> {
      const result = await api.pollPrefill(instanceId);
      return {
        values: result.prefill_values ?? {},
        confidence: result.field_confidence as
          | Record<string, PrefillConfidence>
          | undefined,
      };
    },

    async storeBriefPrefill(args: StoreBriefPrefillArgs): Promise<void> {
      try {
        await api.storeBriefPrefill(
          args.messageIdentity,
          args.prefillValues,
          args.workflowId,
          args.fieldConfidence ?? undefined,
        );
      } catch (err) {
        // Best-effort, matching today's App.tsx::runPrefillFetch behavior —
        // a store failure must not surface to the caller, but IS logged
        // (the core's own runPrefill.ts catches silently; this adapter
        // preserves the add-in's existing warn-log instead of dropping it).
        logger.warn("App: store brief prefill failed", { error: String(err) });
      }
    },
  };
}
